import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Sparkles, Sun, Moon, LogOut, User, Menu, X, ChevronDown, Library as LibraryIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const links = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/generate", label: "Generate" },
  { to: "/predictions", label: "Predictions" },
  { to: "/topics", label: "Topics" },
  { to: "/examiner", label: "Examiner" },
  { to: "/analytics", label: "Progress" },
  { to: "/my-papers", label: "My Papers" },
  { to: "/about", label: "About" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const onDoc = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <header className="sticky top-0 z-50 glass bg-background/70 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </span>
          <span className="font-display font-bold text-lg tracking-tight">PsycGenie</span>
          <span data-testid="nav-beta-badge" className="ml-0.5 rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500 leading-none">Beta</span>
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${pathname.startsWith(l.to) ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
        )}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" data-testid="theme-toggle-btn" onClick={toggle} className="rounded-full">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {user ? (
            <>
              <div className="relative hidden md:block" ref={profileRef}>
                <Button variant="ghost" size="sm" data-testid="nav-profile-btn"
                  onClick={() => setProfileOpen((v) => !v)}
                  className="rounded-full gap-1.5">
                  <User className="h-4 w-4" />
                  <ChevronDown className={`h-3 w-3 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
                </Button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-border bg-card shadow-lg py-1 z-50" data-testid="profile-menu">
                    <Link to="/profile" data-testid="profile-menu-profile" onClick={() => setProfileOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground hover:bg-muted/50">
                      Profile
                    </Link>
                    {isAdmin && (
                      <Link to="/library" data-testid="profile-menu-library" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted/50">
                        <LibraryIcon className="h-3.5 w-3.5" /> Library <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">Admin</span>
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to="/admin/labels" data-testid="profile-menu-admin-labels" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted/50">
                        Label mismatches <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">Admin</span>
                      </Link>
                    )}
                    <button data-testid="profile-menu-logout" onClick={async () => { setProfileOpen(false); await logout(); navigate("/"); }}
                      className="w-full text-left px-4 py-2 text-sm text-destructive hover:bg-muted/50 flex items-center gap-2">
                      <LogOut className="h-3.5 w-3.5" /> Log out
                    </button>
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="rounded-full md:hidden">
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="rounded-full">Login</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/signup")} className="rounded-full glow-btn">Sign Up</Button>
            </>
          )}
        </div>
      </div>
      {user && open && (
        <div className="md:hidden border-t border-border bg-background/95 glass" data-testid="mobile-menu">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2.5 rounded-xl text-sm transition-colors ${pathname.startsWith(l.to) ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/profile" onClick={() => setOpen(false)} data-testid="mobile-nav-profile" className="px-3 py-2.5 rounded-xl text-sm text-muted-foreground">Profile</Link>
            {isAdmin && (
              <Link to="/library" onClick={() => setOpen(false)} data-testid="mobile-nav-library" className="px-3 py-2.5 rounded-xl text-sm text-muted-foreground flex items-center gap-2">
                <LibraryIcon className="h-3.5 w-3.5" /> Library <span className="ml-auto text-[10px] uppercase tracking-wider">Admin</span>
              </Link>
            )}
            <button data-testid="mobile-nav-logout" onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
              className="px-3 py-2.5 rounded-xl text-sm text-left text-muted-foreground">Log out</button>
          </nav>
        </div>
      )}
    </header>
  );
};
